// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * Which pet fields the current role can actually WRITE.
 *
 * This mirrors server rules that live in ada-core. Payload strips a
 * field-level-denied field SILENTLY — it does not error — so without this the
 * portal happily renders an editable input, AutoSave reports "Saved", and the
 * value is simply gone on reload. That is what the chronic-care panel did to
 * every role for five weeks (eng-brain L-029); for vetadmin/siteadmin it also
 * applies to height/weight, which has been true far longer.
 *
 * ⚠️ DRIFT WARNING: this is a MIRROR of another repo's constants, so it can go
 * stale in exactly the way L-002/L-021 describe. It is still the lesser evil —
 * the alternative is lying to the user in the UI — but treat ada-core as the
 * source of truth and re-check these two lists whenever pet access changes.
 *
 * Two guards, and they cover DIFFERENT failures:
 *   - `src/pets/PetEdit.roleAccess.test.tsx` (describe "petFieldAccess mirrors
 *     ada-core") pins the sets below, so an accidental edit HERE is caught.
 *     (This comment used to name a `petFieldAccess.test.ts`, which has never
 *     existed — a reader who greps for it concludes the protection is missing.)
 *   - `scripts/check-pet-field-access-consistency.mjs` compares them against
 *     ada-core's actual sources. That is the one that catches the failure the
 *     tests CANNOT see: ada-core changing while this file stays still, where
 *     both repos are green and the portal quietly starts lying to a role again.
 *
 * Sources (ada-core):
 *   - `src/access/roles.ts:56` PET_FIELD_UPDATE_ROLES
 *     = admin, cms, user, vet, account_manager   → height, weight, weight_kg,
 *       neutered, birth_date, body_condition_score, glucoserange, profile
 *   - `src/collections/Pets/index.ts` inline field access on the clinical
 *     fields = admin, super_admin, vet, cms
 *     ("Only vets + extended admins can edit chronic_conditions — clinical
 *      data, not owner-facing")  → chronic_conditions, target_glucose_low,
 *       target_glucose_high, target_kcal_in_override
 *   - collection-level `update` denies `siteadmin` outright (read-only role).
 */

/** Roles allowed to write the ordinary pet fields (ada-core PET_FIELD_UPDATE_ROLES). */
const PET_BASIC_UPDATE_ROLES = ['admin', 'cms', 'user', 'vet', 'account_manager']

/** Roles allowed to write clinical fields — deliberately NOT vetadmin or account_manager. */
const PET_CLINICAL_UPDATE_ROLES = ['admin', 'super_admin', 'cms', 'vet']

/** siteadmin is read-only on pets at the collection level. */
const PET_UPDATE_DENIED_ROLES = ['siteadmin']

export const canUpdatePetAtAll = (role?: string): boolean =>
  !!role && !PET_UPDATE_DENIED_ROLES.includes(role)

/** height, weight, weight_kg, neutered, birth_date, body_condition_score. */
export const canUpdatePetBasics = (role?: string): boolean =>
  !!role && canUpdatePetAtAll(role) && PET_BASIC_UPDATE_ROLES.includes(role)

/** chronic_conditions, target_glucose_low/high, target_kcal_in_override. */
export const canUpdatePetClinical = (role?: string): boolean =>
  !!role && canUpdatePetAtAll(role) && PET_CLINICAL_UPDATE_ROLES.includes(role)
